import { useMemo } from 'react';

import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { setCookie } from 'cookies-next';

import { ApiError } from '@api/v3';
import {
  getBasketFromCache,
  useBasketId as getBasketIdFromCache,
  BasketType,
} from '@hooks/cache';
import { checkAdvancePaymentSupport, clearBasketStorage } from '@utils/basket';
import { callService } from '@api/remote-call';
import { setSafeLocalStorageItem } from '@utils/localStorage/getSafeLocalStorage';

export const useBasketMutation = () => {
  const { mutate, data } = useMutation(
    (basketId: string) => {
      return callService(
        {
          service: 'BasketsService',
          method: 'getBasket',
          version: 'v3',
        },
        { basketId }
      );
    },
    {
      onSuccess: (basketInfo) => {
        if (typeof window !== 'undefined') {
          setSafeLocalStorageItem('basketDetail', JSON.stringify(basketInfo));
          setSafeLocalStorageItem('basketId', basketInfo?.id);
          setCookie('basketId', basketInfo?.id);
        }
      },
      onError(e) {
        const { detail, title } = (e as ApiError).body?.errors?.[0] ?? {};
        if (/bulunama/.test(detail) || 404 === +title) {
          clearBasketStorage();
        }
      },
    }
  );

  return { mutate, data };
};

export const useBasketQuery = (
  basketId = '',
  options?: UseQueryOptions<BasketType>
) => {
  return useQuery<BasketType>(
    ['basketDetail', basketId],
    () =>
      callService(
        {
          service: 'BasketsService',
          method: 'getBasket',
          version: 'v3',
        },
        { basketId, isArchived: true }
      ),
    {
      enabled: Boolean(basketId),
      onSuccess: (basketInfo) => {
        if (typeof window !== 'undefined') {
          setSafeLocalStorageItem('basketDetail', JSON.stringify(basketInfo));
          setSafeLocalStorageItem('basketId', basketInfo?.id);
          setCookie('basketId', basketInfo?.id);
        }
      },
      onError(e) {
        const { detail, title } = (e as ApiError).body?.errors?.[0] ?? {};
        if (/bulunama/.test(detail) || 404 === +title) {
          clearBasketStorage();
        }
      },
      ...options,
    }
  );
};

const useBasketInformation = (
  bid?: string,
  options?: UseQueryOptions<BasketType>
) => {
  const basketId = bid ?? getBasketIdFromCache();

  const { data: basketInfo, ...rest } = useBasketQuery(basketId, options);
  const basketFromCache = getBasketFromCache();
  const itemsInBasket = useMemo(() => {
    if (!basketInfo) return 0;
    const allItems: number[] = [];

    const { hotels = [], flights = [], tours = [] } = basketInfo ?? {};

    [hotels, flights, tours].forEach((item) => {
      if (item.length > 0) allItems.push(item.length || 0);
    });

    const count =
      basketInfo?.isPackageBasket === true
        ? 1
        : allItems.reduce((partialSum, a) => partialSum + a, 0);

    return count;
  }, [basketInfo]);
  const basket = basketInfo ?? basketFromCache;
  return {
    basketInfo: basket,
    itemsInBasket,
    basketId,
    supportAdvancePayment: checkAdvancePaymentSupport(basket),
    ...rest,
    loading: rest.isInitialLoading,
  };
};
export { getBasketFromCache };

export default useBasketInformation;
